import { Text, IconButton } from "@chakra-ui/react";
import { isReceiveType } from "@syla/shared/types/models/LedgerBase";
import React, { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { FiTrash2 } from "react-icons/fi";
import { amountInvalid } from "../../../../../helper/forms/stringAsNumberInputField";
import { LedgerTypeOption } from "../../../../../helper/transaction/LedgerTypeOptions";
import { useQueryAssetOptions } from "../../../../../store/useQueryAssets";
import { EditTransactionForm } from "../../../../../types/order/addOrderForm";
import { InputField } from "../../../../forms/InputField";
import { LazySingleSelectorField } from "../../../../forms/LazySingleSelectorField";
import { SingleSelectorField } from "../../../../forms/SingleSelectorField";
import { maxAssetsToDisplay } from "../../ledgerInputConstants";
import { TableCell, TableRow } from "./Table";

type LedgerRowName = `updateLedgers.${number}` | `newLedgers.${number}`;

export function EditModeLedgerTableRow({
  name,
  isDisabled,
  validTypes,
  onDelete,
  isDeleteDisabled,
}: {
  name: LedgerRowName;
  validTypes: LedgerTypeOption[];
  isDisabled?: boolean;
  isDeleteDisabled: boolean;
  onDelete: () => void;
}): React.ReactElement {
  const form = useFormContext<EditTransactionForm>();

  const type = useWatch({
    control: form.control,
    name: `${name}.type`,
  });

  const isInLedger =
    !type ||
    isReceiveType({
      type,
    });

  // show user ledgers of the same direction first, in the dropdown
  const sortedValidTypes = useMemo(() => {
    const currentIsInLedger = isInLedger;

    return [...validTypes].sort((a, b) => {
      const aMatches =
        isReceiveType({
          type: a.value,
        }) === currentIsInLedger;

      const bMatches =
        isReceiveType({
          type: b.value,
        }) === currentIsInLedger;

      if (aMatches === bMatches) {
        return 0;
      }

      return aMatches ? -1 : 1;
    });
  }, [validTypes, isInLedger]);

  const queryAssetOptions = useQueryAssetOptions();

  const assetOptions = queryAssetOptions.data ?? EMPTY_ARRAY;

  const assetId = useWatch({
    control: form.control,
    name: `${name}.assetId`,
  });

  const selectedAssetOption = useMemo(() => {
    if (!assetId) {
      return undefined;
    }

    return assetOptions.find((option) => option.value === assetId);
  }, [assetOptions, assetId]);

  return (
    <TableRow>
      <TableCell flexGrow={1.5}>
        <SingleSelectorField
          form={form}
          name={`${name}.type`}
          isRequired
          isDisabled={isDisabled}
          moveSelectedToTop
          buttonProps={{
            width: "full",
          }}
          enableSearch
          options={sortedValidTypes}
          placeholder="Select Ledger Type"
        />
      </TableCell>
      <TableCell>
        {isInLedger ?
          <Text color="green.500">+</Text>
        : <Text color="red.600">-</Text>}

        <InputField
          ml={1}
          form={form}
          name={`${name}.amount`}
          rules={{
            validate: (value) => !amountInvalid({ value }),
          }}
          isRequired
          isDisabled={isDisabled}
          type="text"
          inputMode="decimal"
        />
      </TableCell>
      <TableCell>
        <LazySingleSelectorField
          selectedOption={selectedAssetOption}
          form={form}
          name={`${name}.assetId`}
          moveSelectedToTop
          buttonProps={{
            width: "full",
          }}
          options={assetOptions}
          maxOptionsDisplayed={maxAssetsToDisplay}
          placeholder="Select Asset"
          enableSearch
          isRequired
          isDisabled={isDisabled}
          isLoading={!queryAssetOptions.data}
        />
      </TableCell>
      <TableCell justifyContent="flex-end">
        <InputField
          form={form}
          name={`${name}.marketValue`}
          rules={{
            validate: (value) =>
              !value || !amountInvalid({ value, allowZero: true }),
          }}
          isDisabled={isDisabled}
          placeholder="Optional"
          type="text"
          inputMode="decimal"
        />
      </TableCell>
      <TableCell maxW="2rem">
        <IconButton
          icon={<FiTrash2 />}
          fontSize="1.2rem"
          aria-label="Delete"
          onClick={onDelete}
          isDisabled={isDisabled || isDeleteDisabled}
          colorScheme="red"
          variant="ghost"
        />
      </TableCell>
    </TableRow>
  );
}

const EMPTY_ARRAY = [];
